import { useState, useEffect } from "react";
import { User, Session, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { trackSignupComplete, trackFunnelOAuthComplete } from "@/lib/analytics";
import { consumePostOAuthRedirect } from "@/lib/oauthRedirect";

export interface MFAEnrollResult {
  data: {
    id: string;
    qr_code: string;
    secret: string;
    uri: string;
  } | null;
  error: AuthError | null;
}

export interface MFAVerifyResult {
  data: unknown;
  error: AuthError | null;
}

export interface MFAListResult {
  data: {
    totp: Array<{
      id: string;
      friendly_name?: string;
      factor_type: string;
      status: 'verified' | 'unverified';
      created_at: string;
    }>;
  } | null;
  error: AuthError | null;
}

export interface MFAChallengeResult {
  data: { id: string } | null;
  error: AuthError | null;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_IN") {
          const pendingSignup = sessionStorage.getItem("pending_signup_source");
          if (pendingSignup && session?.user) {
            trackSignupComplete();
            sessionStorage.removeItem("pending_signup_source");
          }

          // Attribute OAuth completion when we have a provider hint stored
          // pre-redirect. Falls back to the session's app_metadata.provider
          // so we still capture completions kicked off from /sign-in.
          if (session?.user) {
            const storedProvider = sessionStorage.getItem("pending_oauth_provider");
            const sessionProvider = (session.user.app_metadata as { provider?: string } | null)?.provider;
            const provider = storedProvider || sessionProvider || "";
            if (provider && provider !== "email") {
              const source = sessionStorage.getItem("pending_oauth_source") || "unknown";
              const createdAt = session.user.created_at ? Date.parse(session.user.created_at) : 0;
              const isNewUser = createdAt > 0 && Date.now() - createdAt < 60_000;
              trackFunnelOAuthComplete(provider, source, isNewUser);
              sessionStorage.removeItem("pending_oauth_provider");
              sessionStorage.removeItem("pending_oauth_source");
            }
          }

          // Restore the exact preview state the user was on before OAuth.
          // We defer to the next tick so React Router has finished mounting
          // and any navigation guards have settled.
          if (session?.user) {
            const target = consumePostOAuthRedirect();
            if (target?.path) {
              const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
              if (current !== target.path) {
                setTimeout(() => {
                  // Use replace so the OAuth callback URL doesn't pollute history
                  window.history.replaceState({}, "", target.path);
                  // Notify React Router by dispatching a popstate event
                  window.dispatchEvent(new PopStateEvent("popstate"));
                }, 0);
              }
            }
          }
        }

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, displayName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName,
        },
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      // Session already expired/invalid server-side — clear local state anyway
      console.warn("Sign out server error (clearing locally):", error.message);
      setUser(null);
      setSession(null);
      return { error: null };
    }
    return { error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/sign-in?reset=true`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  // MFA Methods
  const enrollMFA = async (): Promise<MFAEnrollResult> => {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'Authenticator App',
    });
    
    if (error || !data || data.type !== 'totp') {
      return { data: null, error: error || new Error('Enrollment failed') as unknown as AuthError };
    }
    
    return {
      data: {
        id: data.id,
        qr_code: data.totp.qr_code,
        secret: data.totp.secret,
        uri: data.totp.uri,
      },
      error: null,
    };
  };

  const verifyMFA = async (factorId: string, code: string): Promise<MFAVerifyResult> => {
    const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId,
    });
    
    if (challengeError) {
      return { data: null, error: challengeError };
    }

    const { data, error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });
    return { data, error };
  };

  const unenrollMFA = async (factorId: string) => {
    const { error } = await supabase.auth.mfa.unenroll({
      factorId,
    });
    return { error };
  };

  const listMFAFactors = async (): Promise<MFAListResult> => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error || !data) {
      return { data: null, error };
    }
    return { 
      data: { totp: data.totp || [] }, 
      error: null 
    };
  };

  const challengeMFA = async (factorId: string): Promise<MFAChallengeResult> => {
    const { data, error } = await supabase.auth.mfa.challenge({
      factorId,
    });
    if (error || !data) {
      return { data: null, error };
    }
    return { data: { id: data.id }, error: null };
  };

  const getAuthenticatorAssuranceLevel = async () => {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    return { data, error };
  };

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    // MFA
    enrollMFA,
    verifyMFA,
    unenrollMFA,
    listMFAFactors,
    challengeMFA,
    getAuthenticatorAssuranceLevel,
  };
};
